import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Helmet } from 'react-helmet';
import Navbar from '../components/navBar';
import Footer from '../components/footer';
import OutputBattleTeaser from '../components/OutputBattleTeaser';
import { usePostgresUser } from '../components/utils/PostgresUserContext';

const PAGE_URL = 'https://onikanji.com/output-battle';
const OG_IMAGE = 'https://onikanji.com/og-image.png';
const PAGE_TITLE = 'Output Battle — Japanese Sentence Writing Roguelite | OniKanji';
const PAGE_DESCRIPTION =
    'Play Output Battle on OniKanji: a free roguelite where you defeat bosses by typing natural Japanese sentences. Practice JLPT grammar and vocabulary through AI-graded output, loot, and frontier runs.';
const PAGE_KEYWORDS =
    'Japanese output practice, Japanese sentence writing game, JLPT grammar game, Japanese typing practice, kanji output, OniKanji game, Japanese roguelite, learn Japanese by writing';

// react-snap prerenders this page in an old headless Chromium that can't parse
// the game's modern JS (the iframe is same-origin, so it isn't request-blocked)
// — and crawlers don't need a booted game anyway. Skip the iframe during
// prerender; real browsers mount it on hydration.
const IS_PRERENDER = typeof navigator !== 'undefined' && navigator.userAgent === 'ReactSnap';

function OutputBattlePage() {
    const { user, loading, subscriptionStatus, darkMode } = usePostgresUser();
    const iframeRef = useRef(null);

    const postTheme = useCallback(() => {
        const win = iframeRef.current?.contentWindow;
        if (!win) return;
        win.postMessage({ type: 'onikanji-theme', dark: darkMode }, window.location.origin);
    }, [darkMode]);

    useEffect(() => {
        postTheme();
    }, [postTheme]);

    // Phones: while the player is typing (the game posts onikanji-ob-typing),
    // shrink the iframe to the area above the on-screen keyboard and bring it to
    // the top of the screen, so the game can keep its input row in view. At its
    // normal one-screen height the input sat under the keyboard. Only phone-width
    // games send this message, so desktop is unaffected.
    const [typing, setTyping] = useState(false);
    const [typingHeight, setTypingHeight] = useState(null);

    useEffect(() => {
        const onMessage = (e) => {
            if (e.origin !== window.location.origin || e.source !== iframeRef.current?.contentWindow) return;
            if (e.data && e.data.type === 'onikanji-ob-typing') setTyping(!!e.data.on);
        };
        window.addEventListener('message', onMessage);
        return () => window.removeEventListener('message', onMessage);
    }, []);

    useEffect(() => {
        if (!typing) {
            setTypingHeight(null);
            return undefined;
        }
        const vv = window.visualViewport;
        const fit = () => {
            const el = iframeRef.current;
            if (!el) return;
            setTypingHeight(Math.round(vv ? vv.height : window.innerHeight));
            const top = el.getBoundingClientRect().top + window.scrollY - (vv ? vv.offsetTop : 0);
            window.scrollTo({ top, behavior: 'auto' });
        };
        fit();
        vv?.addEventListener('resize', fit);
        window.addEventListener('resize', fit);
        return () => {
            vv?.removeEventListener('resize', fit);
            window.removeEventListener('resize', fit);
        };
    }, [typing]);

    // Touch screens held sideways (a phone in landscape, ~740x360): one screen
    // minus the navbar left a ~256px sliver with the battle stage off-screen.
    // Give the game the full screen height and scroll the navbar out of view.
    const shortLandscapeQuery = '(pointer: coarse) and (orientation: landscape) and (max-height: 560px) and (min-width: 640px)';
    const [shortLandscape, setShortLandscape] = useState(
        () => typeof window !== 'undefined' && !!window.matchMedia && window.matchMedia(shortLandscapeQuery).matches
    );
    useEffect(() => {
        if (!window.matchMedia) return undefined;
        const mq = window.matchMedia(shortLandscapeQuery);
        const onChange = () => setShortLandscape(mq.matches);
        mq.addEventListener ? mq.addEventListener('change', onChange) : mq.addListener(onChange);
        return () => (mq.removeEventListener ? mq.removeEventListener('change', onChange) : mq.removeListener(onChange));
    }, []);
    useEffect(() => {
        if (!shortLandscape || typing) return;
        const el = iframeRef.current;
        if (el) window.scrollTo({ top: el.getBoundingClientRect().top + window.scrollY, behavior: 'auto' });
    }, [shortLandscape, typing, loading, user]);

    const iframeSrc = `/output-battle/play/?embed=1&theme=${darkMode ? 'dark' : 'light'}`;

    return (
        <div className="font-noto-sans-jp flex flex-col min-h-screen">
            <Helmet>
                <title>{PAGE_TITLE}</title>
                <meta name="description" content={PAGE_DESCRIPTION} />
                <meta name="keywords" content={PAGE_KEYWORDS} />
                <link rel="canonical" href={PAGE_URL} />

                <meta property="og:title" content="Output Battle — Japanese Sentence Writing Game | OniKanji" />
                <meta property="og:description" content={PAGE_DESCRIPTION} />
                <meta property="og:image" content={OG_IMAGE} />
                <meta property="og:url" content={PAGE_URL} />
                <meta property="og:type" content="website" />
                <meta property="og:site_name" content="OniKanji" />
                <meta property="og:locale" content="en_US" />

                <meta name="twitter:card" content="summary_large_image" />
                <meta name="twitter:title" content="Output Battle — Japanese Sentence Writing Game | OniKanji" />
                <meta name="twitter:description" content={PAGE_DESCRIPTION} />
                <meta name="twitter:image" content={OG_IMAGE} />

                <script type="application/ld+json">
                    {JSON.stringify({
                        '@context': 'https://schema.org',
                        '@type': 'BreadcrumbList',
                        itemListElement: [
                            { '@type': 'ListItem', position: 1, name: 'OniKanji', item: 'https://onikanji.com/' },
                            { '@type': 'ListItem', position: 2, name: 'Output Battle', item: PAGE_URL },
                        ],
                    })}
                </script>
                <script type="application/ld+json">
                    {JSON.stringify({
                        '@context': 'https://schema.org',
                        '@type': 'VideoGame',
                        name: 'OniKanji Output Battle',
                        url: PAGE_URL,
                        description: PAGE_DESCRIPTION,
                        applicationCategory: 'Game',
                        genre: 'Educational',
                        operatingSystem: 'Web browser',
                        inLanguage: ['ja', 'en'],
                        isAccessibleForFree: true,
                        playMode: 'SinglePlayer',
                        publisher: {
                            '@type': 'Organization',
                            name: 'OniKanji',
                            url: 'https://onikanji.com',
                        },
                        offers: {
                            '@type': 'Offer',
                            price: '0',
                            priceCurrency: 'USD',
                            availability: 'https://schema.org/InStock',
                        },
                        image: OG_IMAGE,
                    })}
                </script>
            </Helmet>

            <Navbar user={user} subscriptionStatus={subscriptionStatus} darkMode={darkMode} />

            {/* Crawler-visible summary — prerendered into static HTML for SEO */}
            <div className="sr-only">
                <h1>OniKanji Output Battle</h1>
                <p>{PAGE_DESCRIPTION}</p>
            </div>

            <main className="flex-1 bg-mainBackgroundColor pt-4">
                {loading ? <p role="status" className="p-8 text-center">Loading your account…</p> : !user ? (
                    <div className="px-4 pb-12"><OutputBattleTeaser signupRequired /></div>
                ) : !IS_PRERENDER && (
                    <iframe
                        ref={iframeRef}
                        src={iframeSrc}
                        title="OniKanji Output Battle"
                        className="w-full border-0 block"
                        style={{ height: typingHeight ? `${typingHeight}px` : shortLandscape ? '100svh' : 'calc(100svh - 5.5rem - 1rem)' }}
                        onLoad={postTheme}
                        allow="autoplay"
                    />
                )}
            </main>

            <Footer />
        </div>
    );
}

export default OutputBattlePage;
